import { useMutation, useQueryClient } from '@tanstack/react-query'
// import { useToast } from '../state-management/contexts/ToastContext'
import { bookmarkFunds } from '../repository/peer-analyser'
import { usePinRow, useUnpinRow } from '../state-management/fastPinnedRowsStore'
import { usePeerAnalyserStore } from '../state-management/peerAnalyserStore'
import { useToast } from '../state-management/contexts/ToastContext'
import { FundsData } from '../types/peer-analyser'

interface MutationVariables {
  isin: string
  fundCategory: string
  screenSource: string
  action: 'BOOKMARK' | 'UNBOOKMARK'
}

interface MutationContext {
  previousFundsData?: FundsData
}

export function useBookmarkFund(selectedCategoryId: string, onSuccess: (responseData: any) => void) {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  // const { showToast } = useToast()
  const pinRow = usePinRow()
  const unpinRow = useUnpinRow()
  const fundNature = usePeerAnalyserStore((state) => state.fundNature)
  return useMutation({
    mutationFn: (variables: MutationVariables) => {
      return bookmarkFunds({
        isin: variables.isin,
        fundCategory: variables.fundCategory,
        screenSource: variables.screenSource,
        operationType: variables.action === 'UNBOOKMARK' ? 'REMOVE' : 'ADD'
      })
    },
    onMutate: async (newBookmark: MutationVariables) => {
      const queryKey = ['peerAnalyserFunds', selectedCategoryId, fundNature]

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey })

      // Snapshot the previous value
      const previousFundsData = queryClient.getQueryData<FundsData>(queryKey)

      // Optimistically update the Zustand store and React Query cache
      const isCurrentlyPinned = previousFundsData?.selectedFundsIsinList.includes(newBookmark.isin)

      if (isCurrentlyPinned) {
        unpinRow(newBookmark.isin)
      } else {
        if ((previousFundsData?.selectedFundsIsinList.length ?? 0) >= 3) {
          // showToast({ message: 'More than 3 rows cannot be pinned.', zIndex: 10000 })
          return { previousFundsData }
        }
        pinRow(newBookmark.isin)
      }

      if (previousFundsData) {
        const newList = isCurrentlyPinned
          ? previousFundsData.selectedFundsIsinList.filter((isin: string) => isin !== newBookmark.isin)
          : [...previousFundsData.selectedFundsIsinList, newBookmark.isin]

        queryClient.setQueryData(queryKey, {
          ...previousFundsData,
          selectedFundsIsinList: newList
        })
      }

      return { previousFundsData }
    },
    onSuccess: onSuccess,
    onError: (err, newBookmark, context: MutationContext | undefined) => {
      const queryKey = ['peerAnalyserFunds', newBookmark.fundCategory, fundNature]
      if (context?.previousFundsData) {
        queryClient.setQueryData(queryKey, context.previousFundsData)
        showToast({ message: 'Failed to update bookmark. Reverting.' })
      }
    }
    // onSettled: (data, error, variables) => {
    // const queryKey = ['peerAnalyserFunds', variables.fundCategory, 'EQUITY'];
    // queryClient.invalidateQueries({ queryKey });
    // },
  })
}
